import * as React from 'react';
import {Query} from 'react-apollo';

import {useMutation} from '@apollo/react-hooks';

import {LoadingPage} from 'shells/loading-page/loading-page';
import {SectionLabel} from 'shells/typography/section-label';

import type {
    GetContactAudienceMemberships as GetContactAudienceMembershipsQuery,
    GetContactAudienceMembershipsVariables as GetContactAudienceMembershipsQueryVariables,
    DeleteAudienceMember as DeleteAudienceMemberMutation,
    DeleteAudienceMemberVariables as DeleteAudienceMemberMutationVariables,
    AddEntityToAudience as AddEntityToAudienceMutation,
    AddEntityToAudienceVariables as AddEntityToAudienceMutationVariables,
    SessionUser as SessionUserFragment,
} from 'nutshell-graphql-types';

import {GetSessionUser} from '../../../core/get-session-user';
import {AudienceListQuery} from '../../../email-marketing/graphql-wrappers/audience/audience-list-query';

import {addNotification} from '../../../../notifications/index';

import {AddToAudienceButton} from './add-to-audience-button';
import {ContactSubscribedAudiences} from './contact-subscribed-audiences';
import {ContactUnsubscribedAudiences} from './contact-unsubscribed-audiences';
import {useUnsubscribeContactFromAllAudiences} from './graphql/hooks/use-unsubscribe-contact-from-all-audiences';
import ADD_ENTITY_TO_AUDIENCE from './graphql/mutations/add-entity-to-audience.graphql';
import DELETE_AUDIENCE_MEMBER from './graphql/mutations/delete-audience-member.graphql';
import GET_CONTACT_AUDIENCES_MEMBERSHIPS from './graphql/queries/get-contact-audience-memberships.graphql';
import {NutshellMarketingUpsell} from './nutshell-marketing-upsell';
import {PreferenceCenterLinkCopy} from './preference-center-link-copy';
import {UnsubscribeFromAllAudiencesButton} from './unsubscribe-from-all-audiences-button';
import {UnsubscribedFromAllAudiences} from './unsubscribed-from-all-audiences';

class ContactQuery extends Query<
    GetContactAudienceMembershipsQuery,
    GetContactAudienceMembershipsQueryVariables
> {}

type OwnProps = {
    contactId: string;
};

type Props = OwnProps & {
    user: SessionUserFragment | null | undefined;
};

function AudienceInfoComponent(props: Props) {
    const [removeMembership] = useMutation<
        DeleteAudienceMemberMutation,
        DeleteAudienceMemberMutationVariables
    >(DELETE_AUDIENCE_MEMBER);

    const [addMembership] = useMutation<
        AddEntityToAudienceMutation,
        AddEntityToAudienceMutationVariables
    >(ADD_ENTITY_TO_AUDIENCE);

    const {unsubscribeFromAllAudiences} = useUnsubscribeContactFromAllAudiences();

    /**
     * Update the cache after running the mutation to delete an audience
     * membership row. This can update currently subscribed audience memberships
     * as well as unsubscribed audience memberships.
     *
     * @param {string} membershipType A string value representing the membership
     * list type that should be updated. This should be set to the same source
     * that the removed membership came from. It should be `audienceMemberships`
     * only in the event that the contact doesn't have a primary email address,
     * because when that happens we show the list of all audience memberships.
     */
    const removeAudienceMemberUpdate =
        (
            membershipType:
                | 'audienceMembershipsUnsubscribed'
                | 'audienceMembershipsSubscribed'
                | 'audienceMemberships'
        ) =>
        // @ts-expect-error
        (proxy, mutationResults) => {
            // Read the data from our cache for this query.
            const cacheResult = proxy.readQuery({
                query: GET_CONTACT_AUDIENCES_MEMBERSHIPS,
                variables: {personId: props.contactId},
            });

            if (!cacheResult || !cacheResult.contact) {
                return;
            }

            const deletedMembership =
                mutationResults &&
                mutationResults.data &&
                mutationResults.data.audienceMemberDelete &&
                mutationResults.data.audienceMemberDelete.audienceMember;

            if (!deletedMembership || !deletedMembership.deletedTime) return;

            const memberships = cacheResult.contact[membershipType];

            // @ts-expect-error
            const filteredMemberships = memberships.filter((membership) => {
                return membership.id !== deletedMembership.id;
            });

            proxy.writeQuery({
                query: GET_CONTACT_AUDIENCES_MEMBERSHIPS,
                variables: {personId: props.contactId},
                data: {
                    contact: {
                        ...cacheResult.contact,
                        [membershipType]: filteredMemberships,
                    },
                },
            });
        };

    const canAccessMarketing = props.user && props.user.permissions.canAccessMarketing;

    return (
        <ContactQuery
            query={GET_CONTACT_AUDIENCES_MEMBERSHIPS}
            variables={{personId: props.contactId}}
        >
            {({data, loading, error, refetch}) => {
                if ((!data || !data.contact) && loading) {
                    return <div>Loading…</div>;
                }

                const contact = data ? data.contact : undefined;

                if (!contact || error) {
                    return <div>We’re sorry, but we’re having trouble.</div>;
                }

                // If person has unsubscribed from all audiences
                if (contact.hasUnsubscribedFromAllAudiences) {
                    return (
                        <UnsubscribedFromAllAudiences
                            contact={contact}
                            isSupportLogin={NutClientConfig.isSupportLogin}
                        />
                    );
                }

                let membershipListSource = 'audienceMembershipsSubscribed';
                let subscribedMemberships = contact.audienceMembershipsSubscribed;
                const unsubscribedMemberships = contact.audienceMembershipsUnsubscribed;
                const preferenceCenterLink = contact.preferenceCenterLink;
                const memberships = contact.audienceMemberships;

                if (
                    subscribedMemberships.length === 0 &&
                    unsubscribedMemberships.length === 0 &&
                    memberships.length !== 0
                ) {
                    // This is a special case, for when a contact is a
                    // member of memberships, but doesn't have an email
                    // address. In that case, we say they are not
                    // unsubscribed, nor are they subscribed. They just are.
                    subscribedMemberships = memberships;
                    membershipListSource = 'audienceMemberships';
                }

                return (
                    <AudienceListQuery isArchived={false} sortOrder='NAME_ASC'>
                        {({audiences, isLoading: isLoadingAudiences}) => (
                            <AudienceListQuery isArchived={true} sortOrder='NAME_ASC'>
                                {({
                                    audiences: archivedAudiences,
                                    isLoading: isLoadingArchivedAudiences,
                                }) => {
                                    if (isLoadingAudiences || isLoadingArchivedAudiences)
                                        return <LoadingPage />;

                                    return (
                                        <>
                                            {!audiences.length && !archivedAudiences.length ? (
                                                <NutshellMarketingUpsell />
                                            ) : (
                                                <>
                                                    {canAccessMarketing ? (
                                                        <AddToAudienceButton
                                                            onSubmitCreateAudience={(
                                                                audienceId
                                                            ) => {
                                                                return addMembership({
                                                                    variables: {
                                                                        audienceId: audienceId,
                                                                        entityId: contact.id,
                                                                    },
                                                                }).then(() => {
                                                                    // Refetch the list of audience
                                                                    // memberships
                                                                    return refetch();
                                                                });
                                                            }}
                                                            contactName={contact.name}
                                                        />
                                                    ) : undefined}
                                                    <ContactSubscribedAudiences
                                                        membershipListSource={membershipListSource}
                                                        subscribedMemberships={
                                                            subscribedMemberships
                                                        }
                                                        // @ts-expect-error
                                                        onRemove={
                                                            canAccessMarketing
                                                                ? (membershipId: string) => {
                                                                      removeMembership({
                                                                          variables: {
                                                                              audienceMemberId:
                                                                                  membershipId,
                                                                          },
                                                                          update: removeAudienceMemberUpdate(
                                                                              // @ts-expect-error
                                                                              membershipListSource
                                                                          ),
                                                                      }).then(() => {
                                                                          refetch();
                                                                      });
                                                                  }
                                                                : undefined
                                                        }
                                                        contactName={contact.name}
                                                    />
                                                    <ContactUnsubscribedAudiences
                                                        unsubscribedMemberships={
                                                            unsubscribedMemberships
                                                        }
                                                        onRemove={(membershipId: string) => {
                                                            // Removing an unsubscribed contact from an audience is not restricted
                                                            // to only users with email marketing access
                                                            removeMembership({
                                                                variables: {
                                                                    audienceMemberId: membershipId,
                                                                },
                                                                update: removeAudienceMemberUpdate(
                                                                    'audienceMembershipsUnsubscribed'
                                                                ),
                                                            }).then(() => {
                                                                refetch();
                                                            });
                                                        }}
                                                    />
                                                </>
                                            )}
                                            {contact.emailAddresses.all.length > 0 ? (
                                                <UnsubscribeFromAllAudiencesButton
                                                    numSubscribedMemberships={
                                                        subscribedMemberships.length
                                                    }
                                                    onConfirmUnsubscribeAll={() => {
                                                        return unsubscribeFromAllAudiences({
                                                            contactId: props.contactId,
                                                            shouldPrimaryEmailBeSubscribedToAllAudiences:
                                                                false,
                                                        })
                                                            .then(() => {
                                                                refetch();
                                                            })
                                                            .catch(() => {
                                                                addNotification('Whoops!', {
                                                                    body: 'Something went wrong. Please try again later.',
                                                                    icon: 'frowny',
                                                                });
                                                            });
                                                    }}
                                                    contactName={contact.name}
                                                />
                                            ) : undefined}
                                            {preferenceCenterLink ? (
                                                <PreferenceCenterLinkCopy
                                                    link={preferenceCenterLink}
                                                />
                                            ) : undefined}
                                        </>
                                    );
                                }}
                            </AudienceListQuery>
                        )}
                    </AudienceListQuery>
                );
            }}
        </ContactQuery>
    );
}

export function AudienceInfo(props: OwnProps) {
    return (
        <GetSessionUser>
            {({user}) => (
                <div>
                    <SectionLabel>Audiences</SectionLabel>
                    <AudienceInfoComponent {...props} user={user} />
                </div>
            )}
        </GetSessionUser>
    );
}
